import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import './DynamicForm.css';
import { 
	TextField, 
	Snackbar, 
	Alert, 
	Checkbox,
	FormControl,
	FormControlLabel,
	InputAdornment
} from '@mui/material';
import scriptRunnerService from '../../services/scriptRunner.service';

const VALIDATION_PATTERNS = {
	SELECTOR: /^[a-zA-Z0-9_-]{3}$/,
	DOMAIN: /^((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}$/,
	COMPANY_ID: /^[0-9a-f]{24}$/
};

// Field types for special validation
const SELECTOR_FIELD_TYPES = ['selectorNewsletters', 'selectorOutreach'];
const DOMAIN_FIELD_TYPE = 'domain';
const COMPANY_ID_FIELD_TYPE = 'companyId';

const DynamicForm = forwardRef(({ input, selectedScript, onSuccess }, ref) => {
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const [openError, setOpenError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [openSuccess, setOpenSuccess] = useState(false);
	// eslint-disable-next-line no-unused-vars
	const [successMessage, setSuccessMessage] = useState('');
	// eslint-disable-next-line no-unused-vars
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Expose the submit method via ref
	useImperativeHandle(ref, () => ({
		submit: handleSubmit,
		validateAndSubmit: () => {
			const isValid = validateForm();
			if (isValid) {
				handleSubmit();
			} else {
				setErrorMessage('Please fix all validation errors before submitting.');
				setOpenError(true);
			}
			return isValid;
		}
	}));

	// Initialize formData with default values
	useEffect(() => {
		if (input?.length) {
			const initialData = input.reduce((acc, field) => {
				acc[field.id] =
					field.type === 'boolean' ? !!field.default : field.default || '';
				return acc;
			}, {});
			setFormData(initialData);
			setErrors({});
		}
	}, [input]);

	const validateField = (id, value) => {
		const field = input.find(item => item.id === id);
		// Skip validation if field is not required and is empty
		if (field && !field.required && (value === '' || value === null || value === undefined)) {
			return null;
		}
		
		if (!value && field?.required) return "This field is required";
		
		if (SELECTOR_FIELD_TYPES.includes(id)) {
			if (!VALIDATION_PATTERNS.SELECTOR.test(value)) {
				return "Must be exactly 3 characters (letters, numbers, hyphens, underscores)";
			}
		}
		if (id === DOMAIN_FIELD_TYPE) {
			if (!VALIDATION_PATTERNS.DOMAIN.test(value)) {
				return "Please enter a valid domain (e.g., example.com)";
			}
		}
		if (id === COMPANY_ID_FIELD_TYPE) {
			if (!VALIDATION_PATTERNS.COMPANY_ID.test(value)) {
				return "Company ID must be a valid";
			}
		}
		
		return null;
	};

	const handleChange = (e) => {
		const { value, type, checked, id } = e.target;
		const fieldValue = type === 'checkbox' ? checked : value;
		setFormData((prevData) => ({
			...prevData,
			[id]: fieldValue,
		}));
		
		const validationError = validateField(id, fieldValue);
		
		if (fieldValue && errors[id] && !validationError) {
			setErrors(prev => ({ ...prev, [id]: false }));
		} else if (validationError) {
			setErrors(prev => ({ ...prev, [id]: validationError }));
		}
	};

	const validateForm = () => {
		const newErrors = {};
		let isValid = true;
		
		input.forEach(field => {
			const validationError = validateField(field.id, formData[field.id]);
			if (validationError) {
				newErrors[field.id] = validationError;
				isValid = false;
			}
		});
		
		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = () => {
		if (!validateForm()) {
			setErrorMessage('Please fix all validation errors.');
			setOpenError(true);
			return;
		}
		
		setIsSubmitting(true);
		
		scriptRunnerService
			.createJob(formData, selectedScript)
			.then((response) => {
				console.log({ message: 'Job created', response });
				setIsSubmitting(false);
				
				if (typeof onSuccess === 'function') {
					onSuccess();
				}
			})
			.catch((error) => {
				console.error('Error creating job:', error.message);
				setErrorMessage('An error occurred while creating the job.');
				setOpenError(true);
				setIsSubmitting(false);
			});
	};

	const handleCloseError = () => setOpenError(false);
	const handleCloseSuccess = () => setOpenSuccess(false);

	return (
		<div className='dynamic-form'>
			{input.map((field) => (
				<div key={field.id} className='form-group'>
					{field.type === 'boolean' ? (
						<FormControlLabel
							control={
								<Checkbox
									id={field.id}
									name={field.name}
									checked={formData[field.id] ?? !!field.default}
									onChange={handleChange}
								/>
							}
							label={field.name}
							sx={{ marginBottom: '2px' }}
						/>
					) : (
						<FormControl fullWidth margin="dense" error={!!errors[field.id]}>
							<TextField
								type={field.type}
								id={field.id}
								name={field.name}
								label={field.name}
								onChange={handleChange}
								value={formData[field.id] || ''}
								helperText={errors[field.id] === true ? 'This field is required' : errors[field.id] || field.hoverText || ' '}
								error={!!errors[field.id]}
								fullWidth
								required={field.required}
								variant="outlined"
								size="medium"
								margin="dense"
								InputProps={field.type === 'date' ? {
									sx: { 
										paddingRight: '16px',
										'& input': {
											paddingLeft: '8px',
										},
									},
									startAdornment: !formData[field.id] ? (
										<InputAdornment position="start" sx={{ opacity: 0.7 }}>
										</InputAdornment>
									) : null,
								} : undefined}
								sx={(theme) => ({
									marginBottom: '2px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '4px',
										'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
											borderColor: theme.palette.primary.main,
											borderWidth: '1px'
										},
									},
									...(field.type === 'date' ? {
										width: '100%',
										'& input::-webkit-calendar-picker-indicator': {
											position: 'absolute',
											right: '8px',
										},
										'& input': {
											paddingRight: '32px',
										},
									} : {}),
									'& .MuiFormHelperText-root': {
										margin: '1px 0 2px'
									}
								})}
								InputLabelProps={{
									...( field.type === 'date' ? { shrink: true } : {}),
									style: { 
										fontSize: '1rem' 
									}
								}}
							/>
						</FormControl>
					)}
				</div>
			))}
			
			<Snackbar
				open={openError}
				autoHideDuration={6000}
				onClose={handleCloseError}
			>
				<Alert onClose={handleCloseError} severity='error'>
					{errorMessage}
				</Alert>
			</Snackbar>
			<Snackbar
				open={openSuccess}
				autoHideDuration={6000}
				onClose={handleCloseSuccess}
			>
				<Alert onClose={handleCloseSuccess} severity='success'>
					{successMessage}
				</Alert>
			</Snackbar>
		</div>
	);
});

export default DynamicForm;
